import React, { useEffect, useState } from "react";
import Select from "react-select";
import moment from "moment";
import apiClient from "../utils/apiClient";
import { useNavigate, useLocation } from "react-router-dom";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMinus, faChevronCircleLeft } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";

const ProjectRequirement = ({ accessOfDepartments }) => {
  const [date, setDate] = useState("");
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);

  const [typeOption, setTypeOption] = useState(null);

  const sewadarId = localStorage.getItem("sewadarId");
  const code = localStorage.getItem("code");
  const token = localStorage.getItem("token");

  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  const [itemOptions, setItemOptions] = useState([]);

  const [rows, setRows] = useState([
    { item: null, zoneQty: "", storeQty: "", totalQty: 0, uom: "", remarks: "" },
  ]);

  const navigate = useNavigate();
  const location = useLocation();
  const editId = location?.state?.id;

  /* -----------------------------------------------------------
     LOAD DEPARTMENTS 
  ------------------------------------------------------------ */
  useEffect(() => {
    if (accessOfDepartments) {
      const deptArray = accessOfDepartments.split(",").map(d => ({
        value: d.trim(),
        label: d.trim()
      }));
      setDepartments(deptArray);
    }
  }, [accessOfDepartments]);

  /* -----------------------------------------------------------
     LOAD PROJECTS 
  ------------------------------------------------------------ */
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await apiClient.get(`${SERVER_BASE}/area-projects`);
        const options = res.data.map((proj) => ({
          value: proj.id,
          label: proj.projectName,
        }));
        setProjectOptions(options);
      } catch (err) {
        console.error("Error loading projects", err);
      }
    };

    fetchProjects();
  }, []);

  /* -----------------------------------------------------------
     FETCH ITEMS BY DEPARTMENT + TYPE FILTER
  ------------------------------------------------------------ */
  const fetchItems = async () => {
    try {
      const res = await apiClient.get(`${SERVER_BASE}/getItemOptionsByDepartment`, {
        params: { code, accessOfDepartments },
        headers: { Authorization: `Bearer ${token}` },
      });

      let allItems = res.data;

      // ❌ No type → no items
      if (!typeOption) {
        setItemOptions([]);
        return;
      }

      const consumableCodes = ["D", "F", "G"];
      let filtered = [];

      if (typeOption.value === "Consumable") {
        filtered = allItems.filter(item =>
          consumableCodes.includes(item.itemGroupCode)
        );
      } else {
        filtered = allItems.filter(item =>
          !consumableCodes.includes(item.itemGroupCode)
        );
      }

      // Convert to react-select format
      setItemOptions(
        filtered.map((item) => ({
          value: item.printName,
          label: `${item.printName} ${item.additionalNames ? `[${item.additionalNames}]` : ""}`,
          itemId: item.id,
          uom: item.uom,
          itemGroupCode: item.itemGroupCode
        }))
      );
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [typeOption, accessOfDepartments]);

  /* -----------------------------------------------------------
     LOAD DRAFT DATA WHEN EDITING
  ------------------------------------------------------------ */
  const fetchDraftData = async () => {
    if (!editId) return;

    try {
      const response = await apiClient.get(`${SERVER_BASE}/getProjectRequirement/${editId}`);
      const p = response.data;

      setDate(p.date);

      setSelectedDept({ value: p.department, label: p.department });
      setSelectedProject({ value: p.projectId, label: p.projectName });

      // Type is required, assume storing it as p.type
      if (p.type) {
        setTypeOption({ value: p.type, label: p.type });
      }

      await fetchItems();

      const formattedRows = p.items.map((itm) => ({
        item: {
          value: itm.printName,
          label: itm.printName,
          itemId: itm.itemId,
          uom: itm.uom,
        },
        zoneQty: itm.zonalQty,
        storeQty: itm.localQty,
        totalQty: itm.totalQty,
        uom: itm.uom,
        remarks: itm.remarks || "",
      }));

      setRows(formattedRows.length ? formattedRows : [
        { item: null, zoneQty: "", storeQty: "", totalQty: 0, uom: "", remarks: "" }
      ]);

    } catch (err) {
      console.error("Error fetching draft data:", err);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Error loading draft data",
        showConfirmButton: false,
        timer: 2500,
      });
    }
  };

  useEffect(() => {
    fetchDraftData();
  }, [editId]);

  /* -----------------------------------------------------------
     HANDLE ROW CHANGE
  ------------------------------------------------------------ */
  const handleRowChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;

    const zone = Number(updatedRows[index].zoneQty || 0);
    const store = Number(updatedRows[index].storeQty || 0);

    updatedRows[index].totalQty = zone + store;

    setRows(updatedRows);
  };

  /* ----------------------------------------------------------- */
  const addRow = () => {
    setRows([
      ...rows,
      { item: null, zoneQty: "", storeQty: "", totalQty: 0, uom: "", remarks: "" },
    ]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) return;
    setRows(rows.filter((_, i) => i !== index));
  };

  /* -----------------------------------------------------------
     SAVE / UPDATE
  ------------------------------------------------------------ */
  const handleSave = async () => {
    try {
      if (!date)
        return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Please select Date", showConfirmButton: false, timer: 2000 });

      if (!selectedDept)
        return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Please select Department", showConfirmButton: false, timer: 2000 });

      if (!typeOption)
        return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Please select Type", showConfirmButton: false, timer: 2000 });

      if (!selectedProject)
        return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Please select Project", showConfirmButton: false, timer: 2000 });

      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];

        if (!r.item)
          return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: `Select Item in row ${i + 1}`, showConfirmButton: false, timer: 2500 });

        if (Number(r.zoneQty) < 0 || Number(r.storeQty) < 0)
          return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: `Quantity cannot be negative in row ${i + 1}`, showConfirmButton: false, timer: 2500 });

        if (!r.uom)
          return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: `UOM missing in row ${i + 1}`, showConfirmButton: false, timer: 2500 });

        if (!r.remarks.trim())
          return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: `Remarks required in row ${i + 1}`, showConfirmButton: false, timer: 2500 });
      }
  const savedPin = localStorage.getItem("pin");

  if (!savedPin) {
    Swal.fire("Error", "PIN not configured for this user", "error");
    return;
  }

  const { value: enteredPin } = await Swal.fire({
    title: "Enter PIN",
    input: "password",
    inputLabel: "Confirm PIN to proceed",
    inputPlaceholder: "Enter PIN",
    inputAttributes: {
      maxlength: 6,
      autocapitalize: "off",
      autocorrect: "off",
    },
    showCancelButton: true,
    confirmButtonText: "Verify",
  });

  if (!enteredPin) return;

  if (enteredPin != savedPin) {
    Swal.fire("Invalid PIN", "PIN does not match", "error");
    return;
  }
      const payload = {
        date,
        department: selectedDept.value,
        type: typeOption.value,   // ⭐ ADDED IN PAYLOAD
        projectId: selectedProject.value,
        createdBy: sewadarId,
        centerCode: code,
        items: rows.map((r) => ({
          itemId: r.item?.itemId,
          printName: r.item?.value,
          zonalQty: Number(r.zoneQty),
          localQty: Number(r.storeQty),
          totalQty: Number(r.totalQty),
          uom: r.uom,
          remarks: r.remarks,
        })),
      };

      if (editId) {
        await apiClient.put(`${SERVER_BASE}/updateProjectRequirement/${editId}`, payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Updated Successfully", showConfirmButton: false, timer: 2500 });
      } else {
        await apiClient.post(`${SERVER_BASE}/save-project-requirement`, payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Saved Successfully", showConfirmButton: false, timer: 2500 });
      }

      navigate("../ProjectRequirementList");

    } catch (err) {
      console.error(err);
      Swal.fire({ toast: true, position: "top-end", icon: "error", title: "Error Saving", showConfirmButton: false, timer: 2500 });
    }
  };

  /* -----------------------------------------------------------
     RENDER UI
  ------------------------------------------------------------ */
  return (
    <div className="formcontainer mt-5">
      <div className="d-flex align-items-end mb-3">
        <button className="btn btn-secondary btn-sm me-3" onClick={() => navigate("../ProjectRequirementList")}>
          <FontAwesomeIcon icon={faChevronCircleLeft} /> Back
        </button>
        <h5>Project Requirement</h5>
      </div>

      {/* HEADER */}
      <div className="row mb-3">
        <div className="col-md-3">
          <label className="form-label">Date</label>
          <input type="date" className="form-control" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>

        <div className="col-md-3">
          <label className="form-label">Department</label>
          <Select value={selectedDept} options={departments} onChange={setSelectedDept} />
        </div>

        <div className="col-md-3">
          <label className="form-label">Type</label>
          <Select
            value={typeOption}
            options={[
              { value: "Consumable", label: "Consumable" },
              { value: "Non-Consumable", label: "Non-Consumable" },
            ]}
            isDisabled={rows.some(r => r.item !== null)}
            onChange={(opt) => {
              setTypeOption(opt);
              setRows([{ item: null, zoneQty: "", storeQty: "", totalQty: 0, uom: "", remarks: "" }]);
            }}
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Project</label>
          <Select value={selectedProject} options={projectOptions} onChange={setSelectedProject} />
        </div>
      </div>

      {/* COLUMNS */}
      <div className="row fw-bold mb-2">
        <div className="col-md-1">S.No</div>
        <div className="col-md-4">Article</div>
        <div className="col-md-1">Zonal</div>
        <div className="col-md-1">Local</div>
        <div className="col-md-1">Total</div>
        <div className="col-md-1">UOM</div>
        <div className="col-md-2">Remarks</div>
        <div className="col-md-1">Action</div>
      </div>

      {/* ROWS */}
      {rows.map((row, index) => (
        <div key={index} className="mb-3">
          <div className="row align-items-start">

            <div className="col-md-1">
              <input className="form-control" value={index + 1} disabled />
            </div>

            <div className="col-md-4">
              <Select
                isDisabled={!typeOption}
                options={itemOptions}
                value={row.item}
                onChange={(item) => {
                  const updated = [...rows];
                  updated[index].item = item;
                  updated[index].uom = item?.uom || "";
                  setRows(updated);
                }}
              />
            </div>

            <div className="col-md-1">
              <input type="number" className="form-control" value={row.zoneQty}
                onChange={(e) => handleRowChange(index, "zoneQty", e.target.value)} />
            </div>

            <div className="col-md-1">
              <input type="number" className="form-control" value={row.storeQty}
                onChange={(e) => handleRowChange(index, "storeQty", e.target.value)} />
            </div>

            <div className="col-md-1">
              <input className="form-control" value={row.totalQty} disabled />
            </div>

            <div className="col-md-1">
              <input className="form-control" value={row.uom} disabled />
            </div>

            <div className="col-md-2">
              <input className="form-control" placeholder="Remarks" value={row.remarks}
                onChange={(e) => handleRowChange(index, "remarks", e.target.value)} />
            </div>

            <div className="col-md-1 d-flex gap-2">
              <button className="btn btn-success btn-sm" onClick={addRow}>
                <FontAwesomeIcon icon={faPlus} />
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => removeRow(index)}>
                <FontAwesomeIcon icon={faMinus} />
              </button>
            </div>

          </div>
        </div>
      ))}

      <div className="row mt-4">
        <div className="col-md-12 text-end">
          <button className="btn btn-success px-4" onClick={handleSave}>
            Save Requirement
          </button>
        </div>
      </div>

    </div>
  );
};

export default ProjectRequirement;
